import { Quaternion, Vector3 } from 'three'

import { AnyID, LinkedItem, SnapSide } from '@types'

import { getMorphedItemWidth } from './projectItemOnSlabFaces'
import { getRinconeroRegletaMm } from './rinconeroGeometry'
import {
  getObbCenter,
  getSideFacing,
  getSideFacingSlab,
  setSnappedSide,
  type SnappableSlab,
} from './snappedSides'
import { SNAP_SIDES, type SnapGroupNode } from './snapGroups'

/**
 * Reconstruye snappedSides desde la geometría para que `getSnapGroups` pueda trabajar solo
 * con el registro de caras.
 *
 * Hace falta porque snappedSides lo escriben collideItems y projectItemOnSlabFaces al
 * arrastrar, y hay items que nunca pasan por ahí: los de proyectos guardados antes de que
 * existiera el registro, y los que entran por `addProjectItemsToCurrentScene`, que les
 * cambia el id y deja las referencias apuntando a ids que ya no existen.
 *
 * Es una pasada de arranque, no un respaldo permanente: no toca lo que ya está registrado
 * (incluidas las paredes y tabiques de la cara trasera), solo limpia las referencias
 * muertas y rellena las caras que quedan libres. Idempotente: volver a llamarla sobre una
 * escena ya hidratada no cambia nada.
 */

/** Holgura máxima entre dos caras para darlas por pegadas */
const CONTACT_GAP_MM = 30
/** Solape mínimo a lo largo de la cara: rozarse por una esquina no es estar pegado */
const CONTACT_OVERLAP_MM = 20
/** Solape mínimo en altura: es lo que separa un alto de un bajo que caen en la misma vertical */
const VERTICAL_OVERLAP_MM = 10

type Contact = {
  a: SnapGroupNode
  b: SnapGroupNode
  sideA: SnapSide
  sideB: SnapSide
  gap: number
}

type NodeBox = {
  node: SnapGroupNode
  center: Vector3
  quaternion: Quaternion
  xAxis: Vector3
  zAxis: Vector3
  halfWidth: number
  halfThickness: number
  bottom: number
  top: number
  /** Saliente de la regleta: ensancha la holgura de contacto (rinconeros) */
  regleta: number
}

/**
 * @param items todos los objetos de la escena, para saber qué referencias siguen vivas
 * @param candidates items que participan en el registro de caras (visibles, sin frontales
 *        ni itemGroups: el llamador aplica el mismo filtro que use para agrupar)
 * @returns número de caras nuevas registradas
 */
export function rebuildSnappedSides(
  items: { has: (id: AnyID) => boolean; get?: (id: AnyID) => unknown },
  candidates: Iterable<SnapGroupNode>,
): number {
  const nodes = [...candidates].filter((node) => node?.id && node.matrix)

  // Referencias muertas: ids que se remapearon al importar, o vecinos ya borrados.
  for (const node of nodes) {
    const sides = node.snappedSides
    if (!sides) continue
    for (const side of SNAP_SIDES) {
      const otherId = sides[side]
      if (otherId && !items.has(otherId)) delete sides[side]
    }
  }

  const boxes = nodes.map(getNodeBox)

  for (const box of boxes) registerSlabSide(box, items)
  const contacts: Contact[] = []
  for (let i = 0; i < boxes.length; i += 1) {
    for (let j = i + 1; j < boxes.length; j += 1) {
      const contact = getContact(boxes[i], boxes[j])
      if (contact) contacts.push(contact)
    }
  }

  // De menor a mayor holgura: cuando dos items se disputan la misma cara, gana el que
  // está de verdad pegado y el otro se queda sin registrar.
  contacts.sort((first, second) => first.gap - second.gap)

  let registered = 0
  for (const contact of contacts) {
    const { a, b, sideA, sideB } = contact
    if (a.snappedSides?.[sideA] || b.snappedSides?.[sideB]) continue
    // Ya se conocen por otras caras (p.ej. una L registrada al arrastrar): no duplicar.
    if (SNAP_SIDES.some((side) => a.snappedSides?.[side] === b.id)) continue
    if (SNAP_SIDES.some((side) => b.snappedSides?.[side] === a.id)) continue
    setSnappedSide(a, sideA, b.id as AnyID)
    setSnappedSide(b, sideB, a.id as AnyID)
    registered += 1
  }

  return registered
}

/**
 * Registra en el mueble la cara que mira a la losa a la que está pegado.
 *
 * Un mueble contra pared tiene esa cara ocupada igual que si tuviera otro mueble al lado, y
 * frontales y laterales necesitan saberlo. Hasta ahora solo lo traían puesto los muebles
 * que pasan por projectItemOnSlabFaces (los rinconeros, que siempre se proyectan): los que
 * heredan la pared del vecino al pegarse se quedaban con `snappedWall` puesto pero sin
 * ninguna cara registrada.
 *
 * La cara se saca del tramo de la losa, no se da por hecho que sea la trasera: un mueble
 * puede quedar pegado al extremo de un tabique por un lateral.
 */
function registerSlabSide(box: NodeBox, items: { get?: (id: AnyID) => unknown }) {
  const slabId = (box.node as LinkedItem).snappedWall as AnyID | undefined | null
  if (!slabId || !items.get) return
  if (SNAP_SIDES.some((side) => box.node.snappedSides?.[side] === slabId)) return

  const slab = items.get(slabId) as SnappableSlab | undefined
  const side = getSideFacingSlab(box.quaternion, box.center, slab)
  if (side && !box.node.snappedSides?.[side]) setSnappedSide(box.node, side, slabId)
}

function getNodeBox(node: SnapGroupNode): NodeBox {
  const position = new Vector3()
  const quaternion = new Quaternion()
  node.matrix.decompose(position, quaternion, new Vector3())
  const width = getMorphedItemWidth(node)
  const height = node.height || 0
  const thickness = node.thickness || 0
  return {
    node,
    center: getObbCenter(position, quaternion, width, height, thickness),
    quaternion,
    xAxis: new Vector3(1, 0, 0).applyQuaternion(quaternion),
    zAxis: new Vector3(0, 0, 1).applyQuaternion(quaternion),
    halfWidth: width / 2,
    halfThickness: thickness / 2,
    bottom: position.y,
    top: position.y + height,
    // Saliente REAL: el valor guardado va invertido dentro del rango del morph (salvo en
    // KARB). Usarlo en crudo dejaba la holgura corta y el mueble apoyado en el frontal de
    // un rinconero no llegaba a contar como contacto.
    regleta: getRinconeroRegletaMm(node),
  }
}

/**
 * Cara por la que se tocan `a` y `b`, o `null` si no se tocan.
 *
 * Se proyecta el vector entre centros sobre los ejes locales de `a`: la cara buena es
 * aquella cuya separación cabe en la holgura mientras el otro eje solapa de verdad. Un
 * mueble contra el frontal de un rinconero queda separado por el saliente de la regleta,
 * así que ese saliente se suma a la holgura.
 */
function getContact(a: NodeBox, b: NodeBox): Contact | null {
  const verticalOverlap = Math.min(a.top, b.top) - Math.max(a.bottom, b.bottom)
  if (verticalOverlap <= VERTICAL_OVERLAP_MM) return null

  const delta = new Vector3(b.center.x - a.center.x, 0, b.center.z - a.center.z)
  const alongX = a.xAxis.dot(delta)
  const alongZ = a.zAxis.dot(delta)

  const bHalfOnAx =
    b.halfWidth * Math.abs(b.xAxis.dot(a.xAxis)) + b.halfThickness * Math.abs(b.zAxis.dot(a.xAxis))
  const bHalfOnAz =
    b.halfWidth * Math.abs(b.xAxis.dot(a.zAxis)) + b.halfThickness * Math.abs(b.zAxis.dot(a.zAxis))

  const gapX = Math.abs(alongX) - (a.halfWidth + bHalfOnAx)
  const gapZ = Math.abs(alongZ) - (a.halfThickness + bHalfOnAz)

  const maxGap = CONTACT_GAP_MM + Math.max(a.regleta, b.regleta)
  const touchesOnX = gapX <= maxGap && gapZ <= -CONTACT_OVERLAP_MM
  const touchesOnZ = gapZ <= maxGap && gapX <= -CONTACT_OVERLAP_MM
  if (!touchesOnX && !touchesOnZ) return null

  // Si los dos ejes valen (se solapan), la cara real es la del eje menos invadido.
  const onXFace = touchesOnX && (!touchesOnZ || gapX >= gapZ)
  const sideA: SnapSide = onXFace ? (alongX < 0 ? 'left' : 'right') : alongZ < 0 ? 'back' : 'front'

  return {
    a: a.node,
    b: b.node,
    sideA,
    sideB: getSideFacing(b.quaternion, b.center, a.center),
    gap: onXFace ? gapX : gapZ,
  }
}
